// Mřížka produktů kategorie s přetahováním (HTML5 drag & drop, bez knihovny)
// + rychlé „úplně nahoru / dolů". Pořadí = pořadí ve výpisu na e-shopu.
//
// Během tažení se nic nepřeskládává — jen svislá čára ukazuje, kam produkt
// dopadne (před / za kartu podle poloviny, nad kterou je myš). Po puštění se
// karty plynule přesunou (FLIP animace), u okraje okna se stránka sama
// posouvá.

import { useLayoutEffect, useRef, useState } from 'react';

const EDGE_PX = 90; // u horního/dolního okraje okna se při tažení scrolluje
const SCROLL_STEP = 18;

export type OrderItem = {
  guid: string;
  code: string | null;
  name: string;
  image_url: string | null;
  url: string | null;
  hidden: boolean;
  featured: boolean;
  sizes_in_stock?: string[];
  sold_30d?: number;
};

type Props = {
  items: OrderItem[];
  onChange: (items: OrderItem[]) => void;
  /** Poznámka k prodejům (SK: počítáno z CZ objednávek). */
  salesNote?: string;
};

export function CategoryOrderGrid({ items, onChange, salesNote }: Props) {
  // Pruh prodejnosti je poměrný k nejprodávanějšímu produktu v kategorii.
  const maxSold = Math.max(1, ...items.map((i) => i.sold_30d ?? 0));
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  // Kam produkt dopadne: index karty + strana (před / za ní).
  const [drop, setDrop] = useState<{ idx: number; after: boolean } | null>(null);
  const cardRefs = useRef(new Map<string, HTMLDivElement>());
  const prevRects = useRef<Map<string, DOMRect> | null>(null);

  /** Zapamatovat polohy karet před změnou pořadí (FLIP — First). */
  function snapshot() {
    const m = new Map<string, DOMRect>();
    for (const [g, el] of cardRefs.current) m.set(g, el.getBoundingClientRect());
    prevRects.current = m;
  }

  // FLIP — po přeskládání posunout karty zpět na staré místo a nechat je
  // plynule dojet na nové.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reaguje jen na změnu pořadí
  useLayoutEffect(() => {
    const prev = prevRects.current;
    if (!prev) return;
    prevRects.current = null;
    for (const [g, el] of cardRefs.current) {
      const before = prev.get(g);
      if (!before) continue;
      const after = el.getBoundingClientRect();
      const dx = before.left - after.left;
      const dy = before.top - after.top;
      if (!dx && !dy) continue;
      el.style.transition = 'none';
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      requestAnimationFrame(() => {
        el.style.transition = 'transform 280ms cubic-bezier(0.2, 0.7, 0.2, 1)';
        el.style.transform = '';
      });
    }
  }, [items]);

  function moveItem(from: number, to: number) {
    if (from === to) return;
    snapshot();
    const next = [...items];
    const [moved] = next.splice(from, 1);
    if (moved) next.splice(to, 0, moved);
    onChange(next);
  }

  /** Puštění: cílový index po vyjmutí taženého produktu. */
  function commitDrop() {
    if (dragIdx === null || !drop) return;
    let target = drop.after ? drop.idx + 1 : drop.idx;
    if (dragIdx < target) target -= 1;
    moveItem(dragIdx, target);
  }

  /** Při tažení u okraje okna posouvat stránku. */
  function autoScroll(clientY: number) {
    if (clientY < EDGE_PX) window.scrollBy(0, -SCROLL_STEP);
    else if (clientY > window.innerHeight - EDGE_PX) window.scrollBy(0, SCROLL_STEP);
  }

  return (
    <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
      {items.map((it, idx) => (
        <div
          key={it.guid}
          ref={(el) => {
            if (el) cardRefs.current.set(it.guid, el);
            else cardRefs.current.delete(it.guid);
          }}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = 'move';
            setDragIdx(idx);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            autoScroll(e.clientY);
            const rect = e.currentTarget.getBoundingClientRect();
            const after = e.clientX > rect.left + rect.width / 2;
            if (!drop || drop.idx !== idx || drop.after !== after) setDrop({ idx, after });
          }}
          onDrop={(e) => {
            e.preventDefault();
            commitDrop();
            setDragIdx(null);
            setDrop(null);
          }}
          onDragEnd={() => {
            setDragIdx(null);
            setDrop(null);
          }}
          className={`relative bg-surface border rounded-lg p-2 cursor-grab active:cursor-grabbing select-none transition-all ${
            dragIdx === idx ? 'opacity-40' : ''
          } border-border ${it.hidden ? 'opacity-60' : ''}`}
        >
          {/* Čára „sem produkt dopadne" — ne u míst, kde by se nic nezměnilo. */}
          {drop &&
            dragIdx !== null &&
            drop.idx === idx &&
            dragIdx !== idx &&
            !(drop.after && dragIdx === idx + 1) &&
            !(!drop.after && dragIdx === idx - 1) && (
              <div
                className={`absolute -top-1 -bottom-1 w-1.5 rounded-full bg-primary shadow-[0_0_0_3px_rgba(75,188,255,0.25)] z-10 pointer-events-none ${
                  drop.after ? '-right-[9px]' : '-left-[9px]'
                }`}
              />
            )}
          <div className="absolute top-1.5 left-1.5 text-xs font-semibold bg-bg/90 rounded px-1.5 py-0.5 text-text-secondary">
            {idx + 1}.
          </div>
          {it.featured && (
            <div
              className="absolute top-1.5 right-1.5 text-xs"
              title="Produkt týdne — zůstává nahoře"
            >
              🏆
            </div>
          )}
          <div className="aspect-square bg-white rounded flex items-center justify-center overflow-hidden">
            {it.image_url ? (
              <img
                src={it.image_url}
                alt=""
                className="w-full h-full object-contain"
                loading="lazy"
                draggable={false}
              />
            ) : (
              <span className="text-text-muted text-xs">bez fotky</span>
            )}
          </div>
          <div className="mt-1.5 text-xs text-text leading-tight line-clamp-2 min-h-[2rem]">
            {it.name}
          </div>
          <div className="mt-1 flex flex-wrap gap-0.5 min-h-[18px]">
            {(it.sizes_in_stock ?? []).length === 0 ? (
              <span className="text-[10px] text-danger font-semibold">nic skladem</span>
            ) : (
              (it.sizes_in_stock ?? []).map((sz) => (
                <span
                  key={sz}
                  className="text-[10px] leading-none bg-bg border border-border rounded px-1 py-0.5 text-text-secondary"
                >
                  {sz}
                </span>
              ))
            )}
          </div>
          <div className="mt-1.5" title={`Prodáno za 30 dní${salesNote ? ` (${salesNote})` : ''}`}>
            <div className="h-1.5 bg-bg rounded-full overflow-hidden">
              <div
                className="h-full bg-action rounded-full"
                style={{ width: `${Math.round(((it.sold_30d ?? 0) / maxSold) * 100)}%` }}
              />
            </div>
            <div className="text-[10px] text-text-muted mt-0.5">{it.sold_30d ?? 0} ks / 30 dní</div>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-[11px] text-text-muted font-mono truncate">
              {it.code ?? ''}
              {it.hidden && ' · skrytý'}
            </span>
            <span className="flex gap-0.5">
              <button
                type="button"
                onClick={() => moveItem(idx, 0)}
                disabled={idx === 0}
                title="Úplně nahoru"
                className="px-1 text-xs rounded hover:bg-bg disabled:opacity-30 cursor-pointer"
              >
                ⤒
              </button>
              <button
                type="button"
                onClick={() => moveItem(idx, items.length - 1)}
                disabled={idx === items.length - 1}
                title="Úplně dolů"
                className="px-1 text-xs rounded hover:bg-bg disabled:opacity-30 cursor-pointer"
              >
                ⤓
              </button>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
